import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, ChevronRight, Wrench, Award, Heart } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";
import GoogleReviews from "@/components/GoogleReviews";
import { useSiteImages } from "@/lib/siteImages";

const AboutPhotos = () => {
  const { slots } = useSiteImages();
  const photos = [
    { src: slots["about-portrait"], alt: "Dylan Hammerstein — Styne Group", id: "portrait" },
    { src: slots["about-van"], alt: "Styne Group work vehicle", id: "van" },
  ].filter((p) => p.src);
  if (!photos.length) return null;
  return (
    <section className="pb-20 lg:pb-28" data-testid="about-photos">
      <div className={`mx-auto max-w-page px-6 lg:px-10 grid grid-cols-1 gap-6 ${photos.length > 1 ? "md:grid-cols-2" : ""}`}>
        {photos.map((p) => (
          <img key={p.id} src={p.src} alt={p.alt} data-testid={`about-photo-${p.id}`}
            className={`w-full object-cover border border-line ${photos.length > 1 ? "aspect-[4/5]" : "aspect-[16/9]"}`} />
        ))}
      </div>
    </section>
  );
};

const About = () => {
  return (
    <div data-testid="about-page">
      <section className="relative">
        <div className="absolute inset-0 bg-bg" />
        <div className="relative mx-auto max-w-page px-6 lg:px-10 pt-20 pb-24">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">About</span>
          </nav>
          <div className="max-w-3xl">
            <div className="eyebrow mb-5">The Styne Group Story</div>
            <h1 className="font-display text-[clamp(3rem,8vw,7rem)] font-extrabold leading-[0.9] text-ink mb-6">
              Built by a plumber.
              <br />
              <span className="text-gold">For people who&apos;d rather it just worked.</span>
            </h1>
            <p className="text-ink text-xl md:text-2xl font-light leading-snug max-w-2xl">
              Styne Group is a Mount-Evelyn based plumbing, civil and maintenance business
              covering Melbourne&apos;s east and the Yarra Valley. Founded by Dylan Hammerstein
              — licensed plumber, on the tools, on the phone.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Who We Are</div>
            <h2 className="font-display text-4xl md:text-5xl text-ink leading-tight">
              Licensed. Local. Accountable.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 space-y-5 text-muted text-lg leading-relaxed">
            <p>
              Styne Group services homeowners, builders, developers, property managers and
              small commercial clients — from a leaking mixer on a Tuesday night to
              multi-stage civil scope on a residential build.
            </p>
            <p>
              We started the business because plumbing had two problems worth fixing:
              answering the phone, and doing the job the way we&apos;d want it done at
              our own home. That&apos;s the whole brief. It sounds simple. Most companies
              still get it wrong.
            </p>
            <p>
              Behind the scenes we run our own Plumbing Intelligence System — job
              scheduling, materials, invoicing, emergency dispatch, quote engine — so
              nothing slips between the phone call and the finished job.
              You get a tidier experience because we operate a tidier business.
            </p>
          </div>
        </div>
      </section>

      <AboutPhotos />

      <section className="py-20 lg:py-28 bg-elevated">
        <div className="mx-auto max-w-page px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-5">What We Stand For</div>
            <h2 className="font-display text-4xl md:text-6xl text-ink">Our Standard</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line">
            <div className="bg-elevated p-10">
              <Wrench size={28} className="text-gold mb-6" />
              <h3 className="font-display text-2xl text-ink mb-3">Craft</h3>
              <p className="text-muted leading-relaxed">
                Every job gets treated like a portfolio piece. Tidy work, considered
                detail, joints and finishes that hold up. If you&apos;d still be happy
                with it in five years, we&apos;re happy today.
              </p>
            </div>
            <div className="bg-elevated p-10">
              <Award size={28} className="text-gold mb-6" />
              <h3 className="font-display text-2xl text-ink mb-3">Integrity</h3>
              <p className="text-muted leading-relaxed">
                Upfront quotes. No upselling on the day. Same price whether you watch
                us work or leave us to it. Our word&apos;s the whole business — we
                don&apos;t burn it on someone&apos;s vanity.
              </p>
            </div>
            <div className="bg-elevated p-10">
              <Heart size={28} className="text-gold mb-6" />
              <h3 className="font-display text-2xl text-ink mb-3">Service</h3>
              <p className="text-muted leading-relaxed">
                We pick up the phone. We turn up when we say we will. We don&apos;t
                leave a job half-done. Boring, honest, done. The small things
                everyone else forgets about.
              </p>
            </div>
          </div>
        </div>
      </section>

      <GoogleReviews className="bg-bg" />

      <section className="py-20 lg:py-28 bg-elevated border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-6xl text-ink mb-5">Let's Talk</h2>
          <p className="text-muted text-lg max-w-xl mx-auto mb-10">
            Got a project in mind? We'd love to take a look.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={PHONE_HREF} className="btn-gold">
              <Phone size={16} /> Call {PHONE}
            </a>
            <Link to="/contact" className="btn-ghost">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
